import React, { useMemo, useState }  from "react";

import { BaseEditor, createEditor, Descendant, Node } from 'slate'
import { withReact, Slate, Editable, ReactEditor, RenderElementProps } from 'slate-react'
import { HistoryEditor } from 'slate-history'

import Bugsnag from "@bugsnag/js";

import './description.sass'

interface DescriptionProps extends React.BaseHTMLAttributes<HTMLDivElement> {
  xml: string;
}

const parseXml = (xml: string) => {
  const root = new DOMParser().parseFromString(xml, "application/xml").documentElement;
  const isParserError: boolean = root.getElementsByTagName("parsererror").length !== 0;
  if (isParserError) {
    // TODO: add {"metaData": { html: root.outerHTML }})
    Bugsnag.notify(new Error("XML parsing error"));
    return null;
  }
  return root;
}

export type CustomEditor = BaseEditor & ReactEditor & HistoryEditor

export type ParagraphElement = {
  type: 'paragraph'
  children: CustomText[]
}

export type FeatureElement = {
  type: 'feature'
  children: CustomText[]
}

export type HeadingElement = {
  type: 'heading'
  children: CustomText[]
}

export type CustomElement = ParagraphElement | FeatureElement | HeadingElement

export type CustomText = { text: string; }

declare module 'slate' {
  interface CustomTypes {
    Editor: CustomEditor
    Element: CustomElement
    Text: CustomText
  }
}

export const renderElement = ({ children, element }: RenderElementProps) => {
  switch(element.type) {
    case 'heading':
      return <h1>{children}</h1>;
    case 'feature':
      return <h2>{children}</h2>;
    case 'paragraph':
    default:
      return <p>{children}</p>;
  }
};

export const Description: React.FC<DescriptionProps> = ({ xml, ...otherProps }) => {
  const [value, setValue] = useState<Descendant[]>(descriptionFromApi(xml))
  const editor = useMemo(() => withReact(createEditor()), [])
  return (
    <Slate editor={editor} value={value} onChange={setValue}>
      <Editable readOnly className="item-description"
        renderElement={renderElement}
        placeholder="No description has been provided for this instrument."
      />
    </Slate>
  )
}

export const descriptionFromApi = (xml: string): Descendant[] => {
  function parseText(node: Element): string {
    let ret: string = "";
    for (let i = 0; i < node.childNodes.length; i++) {
      const child = node.childNodes[i];
      switch(child.nodeName) {
        case "#text":
          ret += (child.textContent || "").trim();
          break;
        case "newline":
          ret += '\n';
          break;
      }
    }
    return ret;
  }

  let ret: Descendant[] = [];

  const root = parseXml(xml);
  if (root !== null) for (let i = 0; i < root.children.length; i++) {
    const node = root.children[i];
    switch(root.children[i].nodeName) {
      case "heading":
        ret.push({ type: 'heading', children: [{ text: parseText(node) }] });
        break;
      case "feature":
        ret.push({ type: 'feature', children: [{ text: parseText(node) }] });
        break;
      case "paragraph":
        ret.push({ type: 'paragraph', children: [{ text: parseText(node) }] });
        break;
    }
  }

  // Add an empty paragraph node if the description if empty.
  if (ret.length === 0) ret.push({ type: 'paragraph', children: [{ text: '' }] })
  return ret;
}

export const descriptionToApi = (nodes: Node[]) => {
  var xmlDoc = document.implementation.createDocument(null, "description")
  var xmlDescription = xmlDoc.getElementsByTagName("description")[0];
  xmlDescription.setAttribute("version", "1")

  nodes.forEach(n => {
    var xmlNode = xmlDoc.createElement((n as CustomElement).type);
    Node.string(n)
      .split('\n')
      .map(line => xmlDoc.createTextNode(line))
      .forEach((xmlLine, index) => {
        if (index !== 0) xmlNode.appendChild(xmlDoc.createElement('newline'))
        xmlNode.appendChild(xmlLine)
      })
    xmlDescription.appendChild(xmlNode)
  })

  return new XMLSerializer().serializeToString(xmlDoc)
}
